import { Search, ShieldCheck, FileSearch, ClipboardList, ArrowRight } from "lucide-react";
import SectionHeader from "./SectionHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

const SERVICES = [
    {
        icon: Search,
        title: "Supplier Sourcing",
        desc: "We help businesses identify and connect with the right manufacturers, brand owners and authorized distributors across global markets — for both wholesale and Amazon-ready inventory.",
        bullets: [
            "Targeted supplier discovery by category and region",
            "Brand-authorized and manufacturer-direct sourcing",
            "Shortlists with comparable pricing and terms"
        ]
    },
    {
        icon: ShieldCheck,
        title: "Supplier Verification",
        desc: "Before any commitment, we verify the supplier — legal entity, authorizations, references and shipping capability — so you only engage with legitimate partners.",
        bullets: [
            "Business registration & document checks",
            "Brand authorization letters (LOA) where required",
            "Trade references and past-shipment validation"
        ]
    },
    {
        icon: FileSearch,
        title: "Product Research",
        desc: "Category and product research grounded in real market data — helping wholesale buyers and Amazon sellers identify profitable, compliant inventory with realistic margins.",
        bullets: [
            "Demand, competition and margin analysis",
            "Compliance and gating checks (Amazon, customs)",
            "MOQ, lead-time and landed-cost modelling"
        ]
    },
    {
        icon: ClipboardList,
        title: "Procurement Support",
        desc: "Hands-on procurement assistance from purchase order to delivery — managing communication, paperwork, inspections and logistics on your behalf.",
        bullets: [
            "PO drafting, negotiation and contract review",
            "Pre-shipment inspection coordination",
            "Freight, customs and delivery follow-through"
        ]
    }
];

export default function Services() {
    return (
        <section id="services" className="py-20 lg:py-28 bg-background border-y border-border">
            <div className="container-px mx-auto max-w-7xl">
                <SectionHeader
                    eyebrow="What we do"
                    title="Supplier sourcing & procurement, done the right way."
                    description="We help businesses worldwide find, verify and work with reliable suppliers. From the first sourcing brief to the final delivery, every step is handled with documentation, transparency and direct communication."
                    align="center"
                />

                <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-2 gap-5 lg:gap-6">
                    {SERVICES.map((s, idx) => (
                        <Card
                            key={s.title}
                            className="group relative flex flex-col bg-background border-border hover:border-primary/30 hover:shadow-soft transition-smooth"
                        >
                            <CardContent className="flex flex-col h-full p-6 lg:p-8">
                                <div className="flex items-start justify-between">
                                    <span className="grid place-items-center w-12 h-12 rounded-lg bg-accent text-accent-foreground border border-border">
                                        <s.icon className="w-5 h-5" />
                                    </span>
                                    <span className="font-display text-sm font-medium text-muted-foreground">
                                        0{idx + 1}
                                    </span>
                                </div>

                                <h3 className="mt-6 font-display text-xl lg:text-2xl font-semibold text-foreground leading-snug tracking-tight">
                                    {s.title}
                                </h3>
                                <p className="mt-3 text-sm lg:text-base text-muted-foreground leading-relaxed">
                                    {s.desc}
                                </p>

                                <ul className="mt-5 flex flex-col gap-2">
                                    {s.bullets.map((b) => (
                                        <li
                                            key={b}
                                            className="flex items-start gap-2.5 text-sm text-foreground/85 leading-relaxed"
                                        >
                                            <span className="mt-2 w-1.5 h-1.5 rounded-full bg-primary shrink-0" />
                                            <span>{b}</span>
                                        </li>
                                    ))}
                                </ul>

                                <div className="mt-auto pt-6">
                                    <div className="h-px w-full bg-border group-hover:bg-primary/20 transition-smooth" />
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>

                <div className="mt-12 lg:mt-14 flex flex-col sm:flex-row items-center justify-center gap-4 text-center">
                    <p className="text-sm lg:text-base text-muted-foreground max-w-xl">
                        Have a sourcing brief or a supplier you need verified? Share the details — we&apos;ll respond within one business day.
                    </p>
                    <Button
                        asChild
                        className="h-11 px-6 bg-primary text-primary-foreground hover:bg-primary-variant"
                    >
                        <a href="#contact">
                            Send a brief <ArrowRight className="w-4 h-4" />
                        </a>
                    </Button>
                </div>
            </div>
        </section>
    );
}
